import { ref } from 'virtual-vue';
import platform, { PlatformEnum } from '@tencent/wphk-platform-adapter';

/** 触摸采样点，用于计算横向滑动速度 */
interface TouchPoint {
  x: number;
  time: number;
}

interface UseSwipeTouchOptions {
  /** 获取右侧操作区总宽度（px），用于位移夹取与吸附判定 */
  getWidth: () => number;
  /** 是否处于禁用状态 */
  isDisabled: () => boolean;
  /** 获取判定展开的位移比例（操作区宽度的占比） */
  getThreshold: () => number;
  /** 松手吸附回调：shouldOpen 为 true 吸附到展开，否则收起 */
  onSwipeEnd: (_shouldOpen: boolean) => void;
}

/**
 * 从 touch 事件中提取 clientX。
 * H5: event.touches[0].clientX
 * LiteApp: event.detail.clientX（原生组件的 touch 事件结构不同）
 */
function getClientX(event: any): number {
  if (platform.platformType === PlatformEnum.LiteApp) {
    return event?.detail?.clientX ?? 0;
  }
  return event?.touches?.[0]?.clientX ?? event?.changedTouches?.[0]?.clientX ?? 0;
}

/** 从 touch 事件中提取 clientY，用于方向锁定 */
function getClientY(event: any): number {
  if (platform.platformType === PlatformEnum.LiteApp) {
    return event?.detail?.clientY ?? 0;
  }
  return event?.touches?.[0]?.clientY ?? event?.changedTouches?.[0]?.clientY ?? 0;
}

/** 速度阈值（px/ms），向左速度超过即判定展开 */
const SWIPE_VELOCITY = 0.3;
/** 计算速度时取最近此时长（ms）窗口内的采样点 */
const VELOCITY_SAMPLE_WINDOW = 300;
/** 最多保留的触摸采样点数量 */
const MAX_SAMPLE_POINTS = 40;
/** 超过完全展开位置后继续左滑的阻尼系数（越小越「拉不动」） */
const OVERSWIPE_DAMPING = 0.35;
/** 越界额外位移上限（相对操作区宽度的比例） */
const OVERSWIPE_MAX_RATIO = 0.25;

/**
 * SwipeCell 横向触摸交互 composable。
 *
 * 职责：处理 touchstart/move/end，实现仅左滑跟手、方向锁定，
 * 并在松手时基于「位移比例 + 速度」回调吸附结果。
 *
 * 偏移量模型：offset 为 translateX（px），0 为收起，-width 为完全展开；
 * 右侧硬边界为 0，左侧允许越过 -width 带阻尼继续左滑（橡皮筋），松手吸附回 -width。
 */
export function useSwipeTouch(options: UseSwipeTouchOptions) {
  const { getWidth, isDisabled, getThreshold, onSwipeEnd } = options;

  /** 当前 translateX 偏移量（px），驱动内容区位移 */
  const offset = ref(0);
  /** 是否正在触摸拖动中（拖动时移除 transition 以保证跟手） */
  const dragging = ref(false);

  let startX = 0;
  let startY = 0;
  let startOffset = 0;
  /** 是否已完成方向锁定 */
  let locked = false;
  /** 锁定结果：本次手势是否为横向滑动 */
  let isHorizontal = false;
  /** 触摸轨迹采样点，用于在 touchend 时计算滑动速度 */
  const points: TouchPoint[] = [];

  /**
   * 夹取偏移量：
   * - 右侧硬边界 0（不能滑到收起位置右边）；
   * - 左侧允许越过完全展开位置 -width 继续左滑，但施加阻尼并限制上限，
   *   形成「多滑一点、按钮略微拉伸」的橡皮筋效果（松手后由外部吸附回 -width）。
   */
  const clamp = (val: number, width: number): number => {
    if (val >= 0) return 0;
    if (val >= -width) return val;
    const overshoot = -width - val; // 超出完全展开位置的距离（>0）
    const damped = Math.min(overshoot * OVERSWIPE_DAMPING, width * OVERSWIPE_MAX_RATIO);
    return -width - damped;
  };

  /** 记录起始状态，初始化采样数组 */
  const onTouchStart = (event: any) => {
    if (isDisabled()) return;
    startX = getClientX(event);
    startY = getClientY(event);
    startOffset = offset.value;
    locked = false;
    isHorizontal = false;
    dragging.value = true;
    points.length = 0;
    points.push({ x: startX, time: Date.now() });
  };

  /** 实时跟踪手指位置更新偏移量，方向锁定后才接管手势 */
  const onTouchMove = (event: any) => {
    if (isDisabled() || !dragging.value) return;

    const x = getClientX(event);
    const y = getClientY(event);
    const deltaX = x - startX;
    const deltaY = y - startY;

    // 首次移动判定方向：横向位移占优才锁定为横向滑动
    if (!locked) {
      isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
      locked = true;
    }

    // 纵向滑动交还页面滚动
    if (!isHorizontal) return;

    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    const width = getWidth();
    offset.value = clamp(startOffset + deltaX, width);

    const now = Date.now();
    points.push({ x, time: now });
    if (points.length > MAX_SAMPLE_POINTS) {
      points.shift();
    }
  };

  /**
   * 松手后判定吸附目标。
   *
   * 阈值按「相对起始状态的位移」判断（对齐主流组件库）：
   * - 从收起态：左滑超过阈值（按钮宽度占比）或快速左滑 → 展开；
   * - 从展开态：右滑超过阈值或快速右滑 → 收起。
   * 这样从展开态只需回滑约 1/3 按钮宽度即可关闭，而非绝对位置的 3/4。
   */
  const onTouchEnd = () => {
    if (!dragging.value) return;
    dragging.value = false;
    if (!isHorizontal) return;

    const width = getWidth();
    if (width <= 0) {
      onSwipeEnd(false);
      return;
    }

    // 从采样窗口计算横向速度（正值向右，负值向左）
    const endTime = Date.now();
    const recent = points.filter(p => endTime - p.time <= VELOCITY_SAMPLE_WINDOW);
    let velocity = 0;
    if (recent.length >= 2) {
      const first = recent[0];
      const last = recent[recent.length - 1];
      const timeDelta = last.time - first.time;
      if (timeDelta > 0) {
        velocity = (last.x - first.x) / timeDelta;
      }
    }

    const thresholdDistance = width * getThreshold();
    // 相对起始位置的位移：>0 右滑，<0 左滑
    const moved = offset.value - startOffset;
    const startedOpen = startOffset < -width / 2;

    let shouldOpen: boolean;
    if (startedOpen) {
      // 展开态：右滑超阈值或快速右滑则收起，否则保持展开
      const closing = moved > thresholdDistance || velocity > SWIPE_VELOCITY;
      shouldOpen = !closing;
    } else {
      // 收起态：左滑超阈值或快速左滑则展开
      shouldOpen = -moved > thresholdDistance || -velocity > SWIPE_VELOCITY;
    }
    onSwipeEnd(shouldOpen);
  };

  return {
    offset,
    dragging,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  };
}
